
import { useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import {
  peptideProducts,
  merchandiseProducts,
  suppliesProducts,
  bundleProducts,
} from '../../mocks/products';

export default function ShopPage() {
  const [activeCategory, setActiveCategory] = useState<
    'all' | 'peptides' | 'bundles' | 'apparel' | 'supplies'
  >('all');
  const [cart, setCart] = useState<Array<{ id: number; quantity: number }>>([]);
  const [expandedBundle, setExpandedBundle] = useState<number | null>(null);

  const allProducts = [
    ...bundleProducts,
    ...peptideProducts,
    ...merchandiseProducts,
    ...suppliesProducts,
  ];
  const filteredProducts =
    activeCategory === 'all'
      ? allProducts
      : activeCategory === 'peptides'
      ? peptideProducts
      : activeCategory === 'bundles'
      ? bundleProducts
      : activeCategory === 'apparel'
      ? merchandiseProducts
      : suppliesProducts;

  const addToCart = (productId: number) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.id === productId);
      if (existing) {
        return prev.map((item) =>
          item.id === productId
            ? { ...item, quantity: item.quantity + 1 }
            : item,
        );
      }
      return [...prev, { id: productId, quantity: 1 }];
    });
  };

  // Type‑guard for bundle products
  const isBundle = (
    product: any,
  ): product is (typeof bundleProducts)[0] => {
    return 'includes' in product && 'savings' in product;
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-navy via-navy-dark to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="font-display font-bold text-5xl md:text-6xl mb-6">
              Premium Shop
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              Certified peptides and exclusive Sentinel Brands merchandise for
              peak performance
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category navigation – sticky */}
      <section className="py-12 bg-white border-b border-gray-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-4 flex-wrap">
            {[
              { id: 'all', label: 'All Products' },
              { id: 'bundles', label: 'Bundles & Kits' },
              { id: 'peptides', label: 'Peptides' },
              { id: 'apparel', label: 'Apparel' },
              { id: 'supplies', label: 'Supplies' },
            ].map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id as any)}
                className={`px-6 py-3 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  activeCategory === category.id
                    ? category.id === 'bundles'
                      ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
                      : 'bg-electric-blue text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.id === 'bundles' && (
                  <i className="ri-gift-line mr-1.5"></i>
                )}
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products grid */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-baby-blue/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Bundles (always shown when “all” or “bundles” is active) */}
          {(activeCategory === 'all' || activeCategory === 'bundles') && (
            <div className="mb-16">
              {activeCategory === 'all' && (
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gradient-to-r from-amber-500 to-orange-500">
                    <i className="ri-gift-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h2 className="font-display font-bold text-2xl text-navy">
                      Bundles & Starter Kits
                    </h2>
                    <p className="text-sm text-gray-500">
                      Save big with curated peptide bundles
                    </p>
                  </div>
                </div>
              )}
              <div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                data-product-shop
              >
                {bundleProducts.map((product, index) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all group relative border border-amber-200/60"
                  >
                    <div className="absolute top-4 left-4 z-10 flex flex-col gap-1.5">
                      <span className="px-3 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold rounded-full whitespace-nowrap">
                        SAVE ${product.savings.toFixed(2)}
                      </span>
                      <span className="px-3 py-1 bg-navy text-white text-xs font-semibold rounded-full whitespace-nowrap">
                        Bundle
                      </span>
                    </div>
                    <div className="relative h-72 bg-gradient-to-br from-amber-50/60 to-orange-50/40 overflow-hidden">
                      <img
                        src={product.image}
                        alt={product.name}
                        title={`${product.name} - Mixtape Curated peptide bundle`}
                        className="w-full h-full object-contain p-6 group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="font-display font-bold text-xl text-navy mb-2">
                        {product.name}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                        {product.description}
                      </p>

                      {/* Benefits */}
                      <div className="mb-4">
                        <div className="flex flex-wrap gap-2">
                          {product.benefits.map((benefit, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded-full font-medium"
                            >
                              {benefit}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Includes list – collapsible */}
                      <div className="mb-4">
                        <button
                          onClick={() =>
                            setExpandedBundle(
                              expandedBundle === product.id ? null : product.id,
                            )
                          }
                          className="flex items-center gap-1.5 text-xs font-semibold text-navy hover:text-electric-blue transition-colors cursor-pointer"
                        >
                          <i
                            className={`ri-arrow-${
                              expandedBundle === product.id ? 'up' : 'down'
                            }-s-line text-base`}
                          ></i>
                          {expandedBundle === product.id
                            ? 'Hide'
                            : 'Show'}{' '}
                          what&apos;s included ({product.includes.length}{' '}
                          items)
                        </button>
                        {expandedBundle === product.id && (
                          <motion.ul
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className="mt-2 space-y-1.5 pl-1"
                          >
                            {product.includes.map((item, idx) => (
                              <li
                                key={idx}
                                className="flex items-center gap-2 text-xs text-gray-600"
                              >
                                <i className="ri-check-line text-green-500 text-sm"></i>
                                {item}
                              </li>
                            ))}
                          </motion.ul>
                        )}
                      </div>

                      {/* Pricing & CTA */}
                      <div className="flex items-center justify-between">
                        <div className="flex flex-col">
                          <span className="text-xs text-gray-400 line-through">
                            ${product.originalPrice.toFixed(2)}
                          </span>
                          <span className="text-2xl font-bold text-navy">
                            ${product.price}
                          </span>
                        </div>
                        <button
                          onClick={() => addToCart(product.id)}
                          className="px-6 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg text-sm font-medium hover:from-amber-600 hover:to-orange-600 transition-all whitespace-nowrap cursor-pointer"
                        >
                          Add Bundle
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Header for individual products when “all” is selected */}
          {activeCategory === 'all' && (
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-electric-blue">
                <i className="ri-flask-line text-xl text-white"></i>
              </div>
              <div>
                <h2 className="font-display font-bold text-2xl text-navy">
                  Individual Products
                </h2>
                <p className="text-sm text-gray-500">
                  Peptides, apparel & supplies
                </p>
              </div>
            </div>
          )}

          {/* Individual products grid (peptides, apparel, supplies) */}
          {!(activeCategory === 'bundles') && (
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              data-product-shop
            >
              {(
                activeCategory === 'all'
                  ? [...peptideProducts, ...merchandiseProducts, ...suppliesProducts]
                  : activeCategory === 'peptides'
                  ? peptideProducts
                  : activeCategory === 'apparel'
                  ? merchandiseProducts
                  : suppliesProducts
              ).map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all group"
                >
                  <div className="relative h-80 bg-gradient-to-br from-baby-blue/20 to-white overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      title={`${product.name} - Mixtape Curated premium supplies`}
                      className="w-full h-full object-contain p-8 group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4 px-3 py-1 bg-electric-blue text-white text-xs font-semibold rounded-full">
                      {'subcategory' in product
                        ? product.subcategory
                        : product.category}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-display font-bold text-xl text-navy mb-2">
                      {product.name}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {product.description}
                    </p>

                    {/* Optional benefits */}
                    {'benefits' in product && (
                      <div className="mb-4">
                        <div className="flex flex-wrap gap-2">
                          {(product as any).benefits.map(
                            (benefit: string, idx: number) => (
                              <span
                                key={idx}
                                className="px-2 py-1 bg-baby-blue/30 text-navy text-xs rounded-full"
                              >
                                {benefit}
                              </span>
                            ),
                          )}
                        </div>
                      </div>
                    )}

                    {/* Optional details */}
                    {'details' in product && (
                      <div className="mb-4">
                        <div className="flex flex-wrap gap-2">
                          {(product as any).details.map(
                            (detail: string, idx: number) => (
                              <span
                                key={idx}
                                className="px-2 py-1 bg-navy/10 text-navy text-xs rounded-full"
                              >
                                {detail}
                              </span>
                            ),
                          )}
                        </div>
                      </div>
                    )}

                    {/* Optional sizes */}
                    {'sizes' in product && (
                      <div className="mb-4">
                        <p className="text-xs text-gray-500 mb-2">
                          Available sizes:
                        </p>
                        <div className="flex gap-2">
                          {(product as any).sizes.map((size: string) => (
                            <span
                              key={size}
                              className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg text-sm font-medium hover:border-electric-blue hover:text-electric-blue transition-colors cursor-pointer"
                            >
                              {size}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Pricing & CTA */}
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-navy">
                        ${product.price}
                      </span>
                      <button
                        onClick={() => addToCart(product.id)}
                        className="px-6 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Add to Cart
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Floating cart button */}
      {cart.length > 0 && (
        <div className="fixed bottom-8 right-8 z-50">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="bg-navy text-white px-6 py-4 rounded-full shadow-2xl flex items-center gap-3 cursor-pointer hover:bg-navy-dark transition-colors"
          >
            <div className="w-10 h-10 flex items-center justify-center">
              <i className="ri-shopping-cart-line text-2xl"></i>
            </div>
            <div>
              <div className="text-sm font-semibold">
                {cart.reduce((sum, item) => sum + item.quantity, 0)} items
              </div>
              <div className="text-xs text-white/70">View Cart</div>
            </div>
          </motion.div>
        </div>
      )}

      <Footer />
    </div>
  );
}
